import React from "react";
import { Box } from "@mui/material";
import { Whatsapp, Instagram, Facebook, Global } from "iconsax-react";

/**
 * Renders the real app-icon look for WhatsApp/Instagram/Facebook — a solid
 * brand-colored circle with a plain white icon glyph on top (iconsax-react's
 * Linear/outline variant, drawn white) — not a single-color silhouette of the
 * whole logo shape, which is what iconsax's own "Bold" variant is and reads
 * as a blobby, unrecognizable shape at small sizes (found 2026-08-18, 2 tries).
 * @param {string} platform - "whatsapp" | "qr" (WhatsApp Business via QR) | "instagram" | "facebook" | "website"
 * @param {number} size - overall circle diameter in px (default 20)
 * @param {object} sx - MUI sx prop for the icon container
 */
const PlatformIcon = ({ platform, size = 20, sx = {} }) => {
  const iconByPlatform = {
    whatsapp: { Icon: Whatsapp, bg: "#25D366" },
    // WhatsApp Webchat messages: same glyph, black circle.
    whatsapp_web: { Icon: Whatsapp, bg: "#000000" },
    // Real Instagram brand gradient (yellow -> pink/red -> purple/blue, radiating
    // from the bottom-left) instead of a flat color — matches the actual app icon.
    instagram: {
      Icon: Instagram,
      bg: "radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)",
    },
    facebook: { Icon: Facebook, bg: "#1877F2" },
    // Web Chat: same globe glyph + #7C5CFF brand color as its card on the Integrations page.
    website: { Icon: Global, bg: "#7C5CFF" },
    // WhatsApp linked by QR: the WhatsApp Business look -- darker brand green circle, white speech-bubble outline with a
    // "B" inside (drawn here; iconsax has no Business glyph). Rendered by its own branch below.
    qr: { Icon: null, bg: "#128C7E" },
  };

  const match = iconByPlatform[platform];
  if (!match) return null;

  const { Icon, bg } = match;
  const isQr = platform === "qr";
  return (
    <Box
      component="span"
      aria-label={platform}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        width: size,
        height: size,
        borderRadius: "50%",
        background: bg,
        ml: 0.5,
        ...sx,
      }}
    >
      {isQr ? (
        <svg width={Math.round(size * 0.66)} height={Math.round(size * 0.66)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          {/* speech bubble with a tail at the bottom-left, like the WhatsApp glyph */}
          <path
            d="M12 2.5a9.5 9.5 0 0 0-8.2 14.3L2.5 21.5l4.8-1.25A9.5 9.5 0 1 0 12 2.5Z"
            stroke="#ffffff"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <text x="12" y="15.7" textAnchor="middle" fontSize="10.5" fontWeight="800" fontFamily="Arial, Helvetica, sans-serif" fill="#ffffff">
            B
          </text>
        </svg>
      ) : (
      <Icon
        size={Math.round(size * 0.62)}
        color="#ffffff"
        variant="Linear"
        // WhatsApp's own glyph is a speech-bubble WITH a small tail notch at
        // its bottom-left (confirmed in the raw SVG path, not a guess) — that
        // tail shifts the icon's visual weight left/down of its true center,
        // so centering it via the wrapper's flexbox alone still reads as
        // off-center inside a plain round badge. Nudged back optically
        // (Rahim caught this live, 2026-08-20 — most visible at CallWidget's
        // larger size, present at Chats/Calls' smaller size too).
        style={platform === "whatsapp" || platform === "whatsapp_web" ? { transform: "translate(0.5px, -0.5px)" } : undefined}
      />
      )}
    </Box>
  );
};

export default PlatformIcon;
